import { useContext, useEffect } from "react"
import XMPPError from "@xmpp/error"
import AdHocCommands from "@/api/adHocCommands"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import {
  selectDriverNameOrder,
  setDriverNameOrder,
} from "@/reducers/serviceConfigSlice"
import { XmppContext } from "@/stream/xmppClient"
import {
  DRIVER_NAME_ORDER_KEY,
  parseDriverNameOrder,
  type DriverNameOrder,
} from "@/lib/driverNameOrder"

/**
 * The account's driver name order, refreshed from account_config whenever a
 * view using it opens, and a save that every dispatcher of the vhost shares.
 */
export const useDriverNameOrder = () => {
  const { client } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const order = useAppSelector(selectDriverNameOrder)

  useEffect(() => {
    if (!client) return
    let cancelled = false
    AdHocCommands.getAccountConfig(client, DRIVER_NAME_ORDER_KEY)
      .then((value) => {
        if (!cancelled)
          dispatch(setDriverNameOrder(parseDriverNameOrder(value)))
      })
      // An unreachable account_config leaves the last known order in place.
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [client, dispatch])

  const save = async (next: DriverNameOrder) => {
    if (!client || next === order) return
    const previous = order
    dispatch(setDriverNameOrder(next))
    try {
      await AdHocCommands.setAccountConfig(client, DRIVER_NAME_ORDER_KEY, next)
    } catch (e) {
      dispatch(setDriverNameOrder(previous))
      window.appToaster.show({
        message: e instanceof XMPPError ? e.text : (e as Error).message,
        intent: "danger",
        timeout: 3000,
      })
    }
  }

  return { order, save }
}
